/**
 * CustomModes Style03 - Smart Blind preset chips
 */
import { useEffect, useState } from 'react'
import type { TraitControlProps } from './types'
import './CustomModesStyle03.css'

type BlindPresetId = 'p25' | 'p50' | 'p75' | 'sun'

const BLIND_PRESETS: Array<{ id: BlindPresetId; label: string; icon?: string; value: number }> = [
  { id: 'p25', label: '25%', value: 25 },
  { id: 'p50', label: '50%', value: 50 },
  { id: 'p75', label: '75%', value: 75 },
  { id: 'sun', label: '', icon: 'light_mode', value: 100 },
]

function resolvePreset(percent: number): BlindPresetId | null {
  let best: { id: BlindPresetId; diff: number } | null = null
  for (const preset of BLIND_PRESETS) {
    const diff = Math.abs(percent - preset.value)
    if (!best || diff < best.diff) best = { id: preset.id, diff }
  }
  if (!best || best.diff > 8) return null
  return best.id
}

export function CustomModesStyle03({
  state,
  onCommand,
  disabled = false,
}: TraitControlProps) {
  const raw = state?.openClosePercent ?? state?.openPercent ?? state?.position
  const percent = typeof raw === 'number' ? raw : Number(raw)
  const [selectedPreset, setSelectedPreset] = useState<BlindPresetId | null>(
    Number.isFinite(percent) ? resolvePreset(percent) : null
  )

  useEffect(() => {
    const next = Number.isFinite(percent) ? resolvePreset(percent) : null
    setSelectedPreset(next)
  }, [percent])

  const handlePresetClick = (presetId: BlindPresetId) => {
    const preset = BLIND_PRESETS.find((item) => item.id === presetId)
    if (!preset) return
    setSelectedPreset(preset.id)
    void onCommand('setPosition', { openClosePercent: preset.value })
  }

  return (
    <div className="trait-custommodes-style03">
      <span
        className="trait-custommodes-style03__title"
        data-css-slot="blindPresetsTitle"
        data-css-slot-label="Blind Presets Title"
      >
        PRESETS
      </span>
      <div className="trait-custommodes-style03__grid">
        {BLIND_PRESETS.map((preset) => {
          const isActive = selectedPreset === preset.id
          const slotSuffix = preset.id === 'sun' ? 'Sun' : preset.label.replace('%', '')
          return (
            <button
              key={preset.id}
              type="button"
              className={`trait-custommodes-style03__chip${isActive ? ' active' : ''}`}
              onClick={() => handlePresetClick(preset.id)}
              disabled={disabled}
              data-css-slot={`blindPreset${slotSuffix}Chip`}
              data-css-slot-label={`Blind Preset ${slotSuffix} Chip`}
              data-css-state={isActive ? 'on active' : 'off inactive'}
            >
              {preset.icon ? (
                <span
                  className="material-symbols-rounded trait-custommodes-style03__chip-icon"
                  data-css-slot={`blindPreset${slotSuffix}Icon`}
                  data-css-slot-label={`Blind Preset ${slotSuffix} Icon`}
                  data-css-state={isActive ? 'on active' : 'off inactive'}
                >
                  {preset.icon}
                </span>
              ) : (
                preset.label
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
